<template>
  <v-row class="template-placeholder-row">
    <v-col :cols="disabled ? 12 : 9" class="template-placeholder-dropdown-col">
      <v-combobox
        ref="combobox"
        :disabled="disabled"
        style="backdrop-filter: blur(1px)"
        outlined
        v-model="optionDisplay"
        :dense="dense"
        :items="options"
        :label="label || $t(`components.template.editor.placeholder.token`)"
        @change="onChangeSelection"
        autofocus
        :persistent-hint="Boolean(persistentHint)"
        :hint="hint"
      />
    </v-col>
    <v-col v-if="!disabled" cols="3" class="template-placeholder-format-col">
      <v-checkbox v-model="isFormatter" :style="dense ? 'margin-top: 2px' : ''" @change="onChangeSelection">
        <template #label>
          <small> {{ $t(`components.template.editor.placeholder.format`) }} </small>
        </template>
      </v-checkbox>
    </v-col>
    <v-col cols="12" :class="persistentHint ? 'mt-n4' : 'mt-n8'" v-if="isFormatter">
      <v-select
        style="backdrop-filter: blur(1px)"
        outlined
        v-model="formatter"
        :dense="dense"
        :items="formatters"
        @change="onChangeSelection"
        :label="$t(`components.template.editor.placeholder.formatOptions`)"
      >
        <template #item="{ item }"> {{ $t(`components.template.editor.placeholder.formatters.${item}`) }} </template>
        <template #selection="{ item }">
          {{ $t(`components.template.editor.placeholder.formatters.${item}`) }}
        </template>
      </v-select>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { TemplateTokenConfig } from "@/lib/configuration/template-token.configuration";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { TokenCategoriesEnum } from "@/lib/enum/templateEnums/tokenCategories.enum";
import { TemplateFormatterEnum } from "@/lib/enum/TemplateFormatters.enum";
import { ITemplateUrlContext } from "@/lib/interfaces/template/templateContext.interface";
import { IInvoiceParty } from "@/models/invoice-party.entity";
import { IUser } from "@/models/User/IUser";
import { ConfigModule } from "@/store/modules/config";
import { UserModule } from "@/store/modules/me-user.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import { getAllTemplateTokens } from "@/lib/utility/template-token-catalog";
import { DocumentPageEntity } from "@/models/document-template/document-page.entity";

export const TokenUrls: ITemplateUrlContext = {
  request: `${TokenCategoriesEnum.URL}.request`,
  report: `${TokenCategoriesEnum.URL}.report`,
  sharing: `${TokenCategoriesEnum.URL}.sharing`
};

@Component({ components: {} })
export default class TemplateEditorPlaceholderDropdown extends Vue {
  @Prop()
  partnerId?: string;

  @Prop({ default: false })
  dense?: boolean;

  @Prop({ default: false })
  disabled?: boolean;

  @Prop({ default: "" })
  initialOption?: string;

  /**
   * Optional selection of which placeholder types are available for selection
   */
  @Prop() context?: {
    report?: boolean;
    partner?: boolean;
    appraisal?: boolean;
    thg?: boolean;
    user?: boolean;
    billing?: boolean;
    form?: boolean;
    request?: boolean;
    url?: boolean;
    sign?: boolean;
    my?: boolean;
    rental?: boolean;
    repairEstimate?: boolean;
    clearanceApproval?: boolean;
    clearance?: boolean;
    lead?: boolean;
    person?: boolean;
    vehicle?: boolean;
    invoice?: boolean;
  };

  @Prop({ default: false })
  persistentHint?: boolean;

  @Prop({ default: "" })
  label?: string;

  @Prop({ default: "" })
  hint?: string;

  formatter = "";

  isFormatter = false;

  option = this.initialOption ?? "";
  hasEditedOption = false;

  private readonly url = TokenUrls;

  private getAllowedTokenPrefixes(): string[] {
    const prefixes = new Set<string>();
    const context = this.context ?? {};

    const enabled: Array<[string, boolean]> = [
      ["report", Boolean(context.report)],
      ["partner", Boolean(context.partner)],
      ["appraisal", Boolean(context.appraisal)],
      ["thg", Boolean(context.thg)],
      ["user", Boolean(context.user)],
      ["billing", Boolean(context.billing)],
      ["form", Boolean(context.form)],
      ["request", Boolean(context.request)],
      ["url", Boolean(context.url)],
      ["sign", Boolean(context.sign)],
      ["rental", Boolean(context.rental)],
      ["repairEstimate", Boolean(context.repairEstimate)],
      ["clearanceApproval", Boolean(context.clearanceApproval)],
      ["clearance", Boolean(context.clearance)],
      ["lead", Boolean(context.lead)],
      ["person", Boolean(context.person)],
      ["vehicle", Boolean(context.vehicle)],
      ["invoice", Boolean(context.invoice)]
    ];

    for (const [prefix, isEnabled] of enabled) {
      if (isEnabled) prefixes.add(prefix);
    }

    if (!this.partnerId) {
      [
        "report",
        "partner",
        "thg",
        "user",
        "billing",
        "form",
        "request",
        "url",
        "sign",
        "my",
        "appraisal",
        "rental",
        "clearance",
        "clearanceApproval",
        "repairEstimate",
        "lead",
        "person",
        "vehicle",
        "invoice"
      ].forEach((prefix) => prefixes.add(prefix));
      return [...prefixes];
    }

    if (ConfigModule.appContext === AppContextEnum.REPORT_PORTAL) {
      [
        "partner",
        "report",
        "form",
        "request",
        "sign",
        "url",
        "my",
        "appraisal",
        "rental",
        "clearance",
        "clearanceApproval",
        "lead",
        "person",
        "vehicle",
        "repairEstimate",
        "invoice"
      ].forEach((prefix) => prefixes.add(prefix));
      return [...prefixes];
    }

    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL) {
      ["partner", "thg", "user", "billing", "request", "sign", "my"].forEach((prefix) => prefixes.add(prefix));
      return [...prefixes];
    }

    // Default (e.g. report-context) branch: `my` (the logged-in user) is always available here,
    // matching every other branch above, regardless of whether the caller passed context.my.
    prefixes.add("my");

    return [...prefixes];
  }

  get formatters() {
    return Object.values(TemplateFormatterEnum);
  }

  private getTimestampPaths(prefix: string) {
    return {
      created: (prefix + ".timestamp.created") as any,
      lastModified: (prefix + ".timestamp.lastModified") as any,
      modified: (prefix + ".timestamp.modified") as any
    };
  }

  private getTimestampsPaths(prefix: string) {
    return {
      created: (prefix + ".timestamps.created") as any,
      lastModified: (prefix + ".timestamps.lastModified") as any,
      modified: (prefix + ".timestamps.modified") as any
    };
  }

  private getRecipientPaths(prefix: string) {
    return {
      firstName: (prefix + ".recipient.firstName") as any,
      lastName: (prefix + ".recipient.lastName") as any,
      companyName: (prefix + ".recipient.companyName") as any,
      email: (prefix + ".recipient.email") as any
    };
  }

  private getRefPaths(prefix: string) {
    return {
      refType: (prefix + ".ref.type") as any,
      refId: (prefix + ".ref.id") as any
    };
  }

  private getRegistrationPaths(prefix: string) {
    return {
      city: (prefix + ".registration.city") as any,
      firstname: (prefix + ".registration.firstname") as any,
      firstregistrationDay: prefix + ".registration.firstregistrationDay",
      firstregistrationMonth: prefix + ".registration.firstregistrationMonth",
      firstregistrationYear: prefix + ".registration.firstregistrationYear",
      huMonth: prefix + ".registration.huMonth",
      huYear: prefix + ".registration.huYear",
      identificationnumber: prefix + ".registration.identificationnumber",
      manufacturerName: prefix + ".registration.manufacturerName",
      manufacturerType: prefix + ".registration.manufacturerType",
      manufacturerDescription: prefix + ".registration.manufacturerDescription",
      manufacturerNameCode: prefix + ".registration.manufacturerNameCode",
      manufacturerTypeCode: prefix + ".registration.manufacturerTypeCode",
      name: prefix + ".registration.name",
      numberplate: prefix + ".registration.numberplate",
      zipCode: prefix + ".registration.zipCode",
      street: prefix + ".registration.street",
      driveTyp: prefix + ".registration.driveTyp",
      vehicleClass: prefix + ".registration.vehicleClass"
    } as any;
  }

  private getAddressWithGeoPaths(prefix: string) {
    return {
      street: `${prefix}.street`,
      zip: `${prefix}.zip`,
      city: `${prefix}.city`,
      state: `${prefix}.state`,
      countryCode: `${prefix}.countryCode`,
      geo: {
        lat: `${prefix}.geo.lat` as any,
        lng: `${prefix}.geo.lng` as any
      }
    };
  }

  private getInvoicePartyParts(prefix: string): Partial<IInvoiceParty> {
    return {
      name: prefix + ".name",
      email: prefix + ".email",
      phone: prefix + ".phone",
      taxnumber: prefix + ".taxnumber"
    };
  }

  private getRegistrationResultsPaths(prefix: string) {
    return {
      city: (prefix + ".registrationResults.city") as any,
      firstname: (prefix + ".registrationResults.firstname") as any,
      firstregistrationDay: prefix + ".registrationResults.firstregistrationDay",
      firstregistrationMonth: prefix + ".registrationResults.firstregistrationMonth",
      firstregistrationYear: prefix + ".registrationResults.firstregistrationYear",
      huMonth: prefix + ".registrationResults.huMonth",
      huYear: prefix + ".registrationResults.huYear",
      identificationnumber: prefix + ".registrationResults.identificationnumber",
      manufacturerNameCode: prefix + ".registrationResults.manufacturerNameCode",
      manufacturerTypeCode: prefix + ".registrationResults.manufacturerTypeCode",
      name: prefix + ".registrationResults.name",
      numberplate: prefix + ".registrationResults.numberplate",
      zipCode: prefix + ".registrationResults.zipCode",
      street: prefix + ".registrationResults.street",
      driveTyp: prefix + ".registrationResults.driveTyp",
      vehicleClass: prefix + ".registrationResults.vehicleClass"
    } as any;
  }

  private get keysAndTranslations() {
    const allowedPrefixes = this.getAllowedTokenPrefixes();

    const keysAndTranslations = getAllTemplateTokens()
      .filter((key) => {
        if (!allowedPrefixes.length) return true;
        const tokenPrefix = key.split(".")[0];
        return allowedPrefixes.includes(tokenPrefix);
      })
      .map((key) => ({
        key,
        translation: this.$t(`components.template.editor.placeholder.path.${key}`).toString()
      }));

    return keysAndTranslations;
  }

  get options() {
    return this.keysAndTranslations.map((k) => k.translation);
  }

  get optionDisplay(): string {
    if (this.hasEditedOption) {
      return this.option;
    }

    return this.initialOption
      ? DocumentPageEntity.displayLabelForToken(this.initialOption, (key) => this.$t(key).toString())
      : "";
  }

  set optionDisplay(value: string) {
    this.hasEditedOption = true;
    this.option = value;
  }

  private getKeyForTranslation(translation: string) {
    const key = this.keysAndTranslations.find((k) => k.translation === translation)?.key || translation;

    return key;
  }

  onChangeSelection() {
    this.$emit("change", this.select());
  }

  focus() {
    const combobox = this.$refs.combobox as { focus?: () => void } | undefined;
    combobox?.focus?.();
  }

  open() {
    const combobox = this.$refs.combobox as
      | { focus?: () => void; isMenuActive?: boolean; activateMenu?: () => void }
      | undefined;
    combobox?.focus?.();
    if (combobox?.activateMenu) {
      combobox.activateMenu();
      return;
    }
    if (combobox) {
      combobox.isMenuActive = true;
    }
  }

  get me(): IUser {
    return UserModule.user;
  }

  /**
   * Gets the token category from the option
   *
   * @param option
   */
  private getTokenCategory(option: string): TokenCategoriesEnum {
    return option.split(".")[0] as TokenCategoriesEnum;
  }

  /**
   * Verifies that token categories like setting/custom that are not in the user categories are not nested
   *
   * @param option
   */
  private verifyNesting(option: string): void {
    const tokenCategory = this.getTokenCategory(option);

    if (!TemplateTokenConfig.userCategories.includes(tokenCategory)) {
      return;
    }

    if (this.option.split(".").length > 2) {
      const errorString = "User settings should not be nested!";

      this.$toast.error(errorString);
      throw new Error(errorString);
    }
  }

  /**
   * Verifies that the token category is known
   *
   * @param option
   */
  private verifyCategory(option: string) {
    const tokenCategory = this.getTokenCategory(option);

    if (!TemplateTokenConfig.predefinedCategories.includes(tokenCategory)) {
      const errorText =
        "A setting must have one of the following prefixes: " + TemplateTokenConfig.predefinedCategories.join(", ");

      this.$toast.error(errorText);
      throw new Error(errorText);
    }
  }

  private formatPlaceholder(placeholder: string, formatter: string) {
    if (formatter === TemplateFormatterEnum.ESCAPE) {
      return `{{{${placeholder}}}}`;
    }

    placeholder = `{{${placeholder}}}`;
    let placeholderFormatterPrefix = "";
    let placeholderFormatterSuffix = "";
    if (this.isFormatter && formatter) {
      placeholderFormatterPrefix = `{{#${formatter}}} `;
      placeholderFormatterSuffix = ` {{/${formatter}}}`;
    }

    return `${placeholderFormatterPrefix}${placeholder}${placeholderFormatterSuffix}`;
  }

  /**
   * Verify the token category and return it formatted
   */
  select() {
    const token = this.getKeyForTranslation(this.option);

    this.verifyNesting(token);
    this.verifyCategory(token);

    return this.formatPlaceholder(token, this.formatter);
  }
}
</script>

<style scoped>
/* Fix margin below placeholder dropdown and align format button */
.template-placeholder-row {
  margin-bottom: 0 !important;
}
.template-placeholder-dropdown-col > .v-input,
.template-placeholder-dropdown-col > .v-input--is-focused,
.template-placeholder-dropdown-col > .v-combobox {
  margin-bottom: 4px !important;
}
.template-placeholder-dropdown-col {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
.template-placeholder-format-col {
  display: flex;
  align-items: center !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  height: 100%;
}
.template-placeholder-format-col .v-input--selection-controls {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-items: center !important;
}
.scrollable {
  overflow-y: auto;
}
</style>
